import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { CheckboxValue } from "capitalrx-components-2.0/lib/forms";
import { Body, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Button, Spinner } from "capitalrx-components-2.0/lib/general";

import { getPACaseList } from "@/api";
import { AuthenticationContext } from "@/context";
import { getThemeURL } from "@/utils";
import PAResultList from "@/components/PAListForm/PAResultList";
import { PACaseFilterBar } from "@/components/PAListForm/PACaseFilterBar";

interface PACaseListSearchProps {
  pageNumber: number;
  setPageNumber: (page: number) => void;
  externalMemberID: string[];
  patientDOB?: string;
  returnUrl: string;
}
const outcomeMap: Record<string, string> = {
  "In Progress": "in_progress",
  "Approved": "approved",
  "Denied": "denied",
};

export const PACaseListSearch = (props: PACaseListSearchProps) => {

  // Props
  const { pageNumber, setPageNumber, externalMemberID, patientDOB, returnUrl } = props;

  // Context
  const { isUserLoggedIn, getAuthToken, getUser } = useContext(AuthenticationContext);
  const navigate = useNavigate();

  // useState initializers
  const [loading, setLoading] = useState<boolean>(false);
  const [paResults, setPAResults] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const [filteredPAResults, setFilteredPAResults] = useState(paResults);
  const [actionRequired, setActionRequired] = useState<CheckboxValue>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const toggleOutcome = (label: string) => {
    setSelectedOutcomes((prev) => (prev.includes(label) ? [] : [label]));
  };

  // Helper function to normalize case status based on case completion

  const normalizeOutcome = (result: any): string => {
    if (!result.case.is_complete) {
      return "in_progress"; // override everything if incomplete
    }

    const rawOutcome = result.case_outcome?.outcome;
    return !rawOutcome || rawOutcome === "closed" ? "in_progress" : rawOutcome;
  };

  // Apply all filters whenever any dependency changes

  useEffect(() => {
    const results = paResults.filter((result) => {
      const outcome = normalizeOutcome(result);

      // Outcome filter (single selection) — map label to normalized value for comparison
      if (selectedOutcomes.length > 0 && outcome !== outcomeMap[selectedOutcomes[0]]) {
        return false;
      }

      // Action Required filter
      if (actionRequired && result.case.queue !== "outreach") return false;

      // Search filter
      if (searchTerm.trim().length > 0) {
        const idMatch = result.case.case_id.toString().includes(searchTerm);
        const drugMatch = result.case.drug_name.toLowerCase().includes(searchTerm.toLowerCase());
        if (!idMatch && !drugMatch) return false;
      }

      return true;
    });

    setFilteredPAResults(results);
  }, [paResults, actionRequired, searchTerm, selectedOutcomes]);

  useEffect(() => {
    const fetchCases = async () => {
      // Not logged in, show lookup only
      if (!isUserLoggedIn()) {
        return;
      }

      /* User's provider record must be active status
            should we tell the user this with some verbiage? */
      const user = await getUser();
      if (user.provider_enrollment_status === "active") {
        setLoading(true);
        try {
          const authToken = getAuthToken();
          const requestPayload = {
            is_action_required: false,
            results_per_page: 144,
            page_number: pageNumber,
            external_member_id: externalMemberID,
          };

          const response = await getPACaseList(requestPayload, authToken);
          setPAResults(response?.results);
          setFilteredPAResults(response?.results);
        } catch (error) {
          console.error("Failed to fetch cases:", error);
        } finally {
          setLoading(false);
        }
      };
    };

    fetchCases();
  }, [pageNumber, isUserLoggedIn, getUser]);

  return (
    <Stack>
      <Shelf>
        <Subtitle>PA Case List</Subtitle>
        <Shelf.Spacer />
        <Button appearance="secondary" onClick={() => navigate(`${getThemeURL("PA_CREATE")}?back_url=${encodeURIComponent(returnUrl)}`)}>Start/Renew A Case</Button>
      </Shelf>
      <PACaseFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedStatuses={selectedOutcomes}
        onStatusToggle={toggleOutcome}
        actionRequired={actionRequired}
        onActionRequiredChange={setActionRequired}
        searchInstructions="Search by case ID or drug name"
      />
      {loading && <Spinner size="large" />}
      {!loading && paResults?.length === 0 && <Body>Member has no prior authorizations</Body>}
      {!loading && paResults && <PAResultList results={filteredPAResults} page={pageNumber} setPage={setPageNumber} singleMember={true} patientDOB={patientDOB} backUrl={returnUrl} />}
    </Stack>
  );
};
